import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Container, StyledBoxContainer, Top } from '../../../styles'
import { DefaultAdminPage } from '../../../../DefaultPages'
import {
  BoxTitle,
  Content,
  GroupRadios,
  TopContainer,
  WrapperAnswers
} from './styles'
import {
  Breadcrumbs,
  Radio,
  Search,
  SequenceQuestion,
  Button,
  Typography
} from '../../../../../components'
import { PaginationOrder } from '../../../../../constants/pagination'
import { useQuery } from 'react-query'
import ecbrApi from '../../../../../services/ecbrApi'
import { QuestionsAnswersStatus } from '../../../../../constants/questions'
import ErrorMessage from '../../../../../components/Form/ErrorMessage'
import { dateTypes } from '../../../../../constants/date'
import useUrlParam from '../../../../../hooks/useUrlParam'
import { useModal } from '../../../../../hooks/useModal'
import { ResponseSendModal } from '../../../../../modals/ResponseSendModal'
import ResponseSendImageQuestionary from '../../../../../assets/Icons/ResponseSendImageQuestionary'
import {
  ContainerQuestionMarkWithBorder,
  ModalContentText,
  TitleModal,
  ModalBodyContainer
} from '../../../../../modals/styles'
import dayjs from '../../../../../helpers/dayjs'
import Checkbox from '../../../../../components/Checkbox'

const pagination = { page: 1, take: 20 }

const AnswersReview = () => {
  const [error, setError] = useState('')
  const [brandId, certificateId] = useUrlParam(
    '/admin/review/:id/certificates/:id/answers'
  )
  const [searchParameters, setSearchParameters] = useState({
    ...pagination,
    order: PaginationOrder.ASC,
    searchValue: '',
    brandIds: [Number(brandId)],
    certificateId: Number(certificateId)
  })
  const [reviewedAnswers, setReviewedAnswers] = useState([])
  const [_loading, setLoading] = useState(true)
  const [checkAllApproved, setCheckAllApproved] = useState(false)
  const [renderSendResponseModal, openSendResponseModal] =
    useModal(ResponseSendModal)

  const showSendResponseModal = useCallback(
    ({ onConfirm, ...rest } = {}) => {
      openSendResponseModal({
        ...rest,
        onConfirm,
        body: (
          <ModalBodyContainer>
            <ContainerQuestionMarkWithBorder>
              <ResponseSendImageQuestionary />
            </ContainerQuestionMarkWithBorder>
            <TitleModal>Atenção!</TitleModal>
            <ModalContentText>
              Deseja salvar a correção das respostas?
            </ModalContentText>
          </ModalBodyContainer>
        )
      })
    },
    [openSendResponseModal]
  )

  const {
    data: response,
    isFetching,
    isLoading,
    refetch
  } = useQuery(
    ['answers', searchParameters],
    async ({ queryKey: [_, params] }) => {
      try {
        setLoading(true)
        const resp = await ecbrApi.questionsAnswers.getPendingApproval(params)

        const { data: dataAnswers } = resp.data

        const filteredAnswers = dataAnswers.filter(
          (answer) =>
            answer?.question?.description
              ?.toLowerCase()
              .includes(params.searchValue.toLowerCase()) ||
            answer?.question?.topic?.name
              ?.toLowerCase()
              .includes(params.searchValue.toLowerCase()) ||
            answer?.question?.topic?.pillar?.name
              ?.toLowerCase()
              .includes(params.searchValue.toLowerCase())
        )

        setLoading(false)
        return {
          ...resp,
          data: {
            ...resp.data,
            data: filteredAnswers
          }
        }
      } catch (err) {
        setLoading(false)
        setError(err.message)
      }
    },
    {
      onSuccess: () => {
        setError('')
      },
      onError: () => {
        setError(
          'Não foi possível carregar as respostas pendentes de aprovação.'
        )
      }
    }
  )

  const {
    data: answers,
    hasNextPage,
    hasPreviousPage,
    currPage
  } = useMemo(() => {
    if (response) {
      const { data: results, pagination: paginationData } = response?.data || {}
      const { page, perPage, total } = paginationData || {}

      return {
        data: results,
        hasNextPage: page < Math.ceil(total / perPage),
        hasPreviousPage: page > 1,
        isLastPage: page === total,
        hasItems: results.length > 0,
        perPage,
        currPage: page,
        totalPages: Math.ceil(total / perPage),
        totalItems: total
      }
    }
    return {
      data: [],
      hasNextPage: false,
      hasPreviousPage: false,
      isLastPage: false,
      hasItems: false,
      perPage: 0,
      currPage: 0,
      totalPages: 0,
      totalItems: 0
    }
  }, [response])

  const handleSaveAllReviewedAnswers = async () => {
    showSendResponseModal({
      onConfirm: async () => {
        setLoading(true)
        try {
          reviewedAnswers.forEach(async (answer) => {
            await ecbrApi.questionsAnswers.updateStatus(
              answer.id,
              answer.status
            )
          })
          setLoading(false)
          setTimeout(() => {
            refetch()
          }, 1000)
          setReviewedAnswers([])
        } catch (err) {
          setLoading(false)
          setError(
            'Não foi possível salvar as respostas pendentes de aprovação.'
          )
        }
      }
    })
  }

  useEffect(() => {
    if (isLoading || isFetching) {
      setError('')
    }
  }, [isFetching, isLoading])

  const isAllAnswersApproved = useMemo(() => {
    const approvedArr = Array(answers.length)
      .fill(false)
      .map((_, index) => {
        const answer = answers[index]

        return (
          answer?.status === QuestionsAnswersStatus.APPROVED ||
          reviewedAnswers.find(({ id }) => id === answer.id)?.status ===
            QuestionsAnswersStatus.APPROVED
        )
      })

    return approvedArr.every((approved) => approved)
  }, [answers, reviewedAnswers])

  useEffect(() => {
    setCheckAllApproved(isAllAnswersApproved)
  }, [isAllAnswersApproved])

  const checkAllAsApproved = useCallback(() => {
    let reviewedAnswersCopy = [...reviewedAnswers]

    answers.forEach((answer) => {
      const reviewedAnswer = reviewedAnswers.find(({ id }) => id === answer.id)

      if (reviewedAnswer?.status === QuestionsAnswersStatus.REPROVED) {
        reviewedAnswersCopy = reviewedAnswersCopy.filter(
          ({ id }) => id !== answer.id
        )
      }

      if (!reviewedAnswersCopy.find(({ id }) => id === answer.id)) {
        reviewedAnswersCopy.push({
          id: answer.id,
          status: QuestionsAnswersStatus.APPROVED
        })
      }
    })

    setReviewedAnswers(reviewedAnswersCopy)
  }, [answers, reviewedAnswers])

  const handleChangeAllApproved = useCallback(
    (checked) => {
      if (checked) {
        checkAllAsApproved()
      } else {
        setReviewedAnswers([])
      }
    },
    [checkAllAsApproved]
  )

  const handleChangePage = (page) => {
    setSearchParameters({
      ...searchParameters,
      page
    })
  }

  const handleChangeAnswer = (answer, status) => {
    setReviewedAnswers([
      ...reviewedAnswers.filter((item) => item.id !== answer.id),
      {
        id: answer.id,
        status
      }
    ])
  }

  const handleCheckedAnswer = (answer, status) => {
    const item = reviewedAnswers.find((item) => item.id === answer.id)
    return item?.status === status
  }

  return (
    <DefaultAdminPage page="revisao">
      {renderSendResponseModal}
      <Container>
        <Top>
          <Breadcrumbs />
        </Top>

        <BoxTitle>
          <hgroup>
            <Typography type="mainTitle">Revisão do questionário</Typography>
          </hgroup>
        </BoxTitle>

        <Content>
          <h2>Pesquise aqui</h2>
          <TopContainer>
            <Search
              placeholder="pesquisar questionário"
              onSearch={(value) => {
                setSearchParameters({
                  ...searchParameters,
                  searchValue: value
                })
              }}
            />
            <Checkbox
              name="checkAllApproved"
              label="Marcar todas como aprovadas"
              checked={checkAllApproved}
              onChange={(e) => handleChangeAllApproved(e.target.checked)}
              width="max-content"
            />
          </TopContainer>
          {!isLoading && !isFetching && !answers.length && (
            <StyledBoxContainer>
              <p>Nenhuma resposta pendente de aprovação.</p>
            </StyledBoxContainer>
          )}
          {answers?.map?.((answer) => (
            <StyledBoxContainer key={answer.id}>
              <header>
                <SequenceQuestion
                  itensSequence={[
                    answer.question.topic.pillar.name,
                    answer.question.topic.name
                  ]}
                />
                <span>
                  Respondido por: <strong>{answer.user.name}</strong> em{' '}
                  <strong>
                    {dayjs(answer.createdAt).format(dateTypes.dateBR)}
                  </strong>
                </span>
              </header>
              <p>{answer.question.description}</p>
              <WrapperAnswers>
                <textarea disabled value={answer.value} />
                <GroupRadios>
                  <li>
                    <Radio
                      label="Aprovado"
                      name={`${answer.id}-review`}
                      onChange={() =>
                        handleChangeAnswer(
                          answer,
                          QuestionsAnswersStatus.APPROVED
                        )
                      }
                      checked={handleCheckedAnswer(
                        answer,
                        QuestionsAnswersStatus.APPROVED
                      )}
                    />
                  </li>
                  <li>
                    <Radio
                      label="Reprovado"
                      name={`${answer.id}-review`}
                      onChange={() =>
                        handleChangeAnswer(
                          answer,
                          QuestionsAnswersStatus.REPROVED
                        )
                      }
                      checked={handleCheckedAnswer(
                        answer,
                        QuestionsAnswersStatus.REPROVED
                      )}
                    />
                  </li>
                  <li>
                    <Button
                      text="Limpar"
                      variant="outlined"
                      onClick={() => {
                        setReviewedAnswers([
                          ...reviewedAnswers.filter(
                            (item) => item.id !== answer.id
                          )
                        ])
                      }}
                      disabled={
                        reviewedAnswers.find((item) => item.id === answer.id)
                          ?.status === QuestionsAnswersStatus.PENDING
                      }
                    />
                  </li>
                </GroupRadios>
              </WrapperAnswers>
            </StyledBoxContainer>
          ))}

          {error && <ErrorMessage>{error}</ErrorMessage>}
          <footer>
            <div>
              <Button
                type="button"
                variant="outlinedLinkDefault"
                text="Anterior"
                width="120px"
                disabled={!hasPreviousPage}
                onClick={() => handleChangePage(currPage - 1)}
              />
            </div>
            <div>
              <Button
                type="button"
                variant="primary"
                text="Salvar"
                width="120px"
                disabled={!reviewedAnswers.length}
                onClick={handleSaveAllReviewedAnswers}
              />
              <Button
                type="button"
                variant="outlinedLinkDefault"
                text="Próximo"
                width="120px"
                disabled={!hasNextPage}
                onClick={() => handleChangePage(currPage + 1)}
              />
            </div>
          </footer>
        </Content>
      </Container>
    </DefaultAdminPage>
  )
}

export default AnswersReview
